import {
	MutationCache,
	QueryCache,
	QueryClient,
	QueryClientProvider,
} from "@tanstack/react-query";
import { meQuery } from "@/auth/queries";
import { ContentError } from "@/content/loader";

/** Errors a retry will not fix: no session, forbidden, not found, rate limited (handled apart). */
const FINAL_STATUSES = new Set([401, 403, 404, 429]);

export const isFinalError = (error: unknown): boolean =>
	error instanceof ContentError &&
	error.status !== undefined &&
	FINAL_STATUSES.has(error.status);

interface ContextOptions {
	/** Called when an API call answers 401 (the session expired): back to the login page. */
	onUnauthorized?: () => void;
}

export function getContext({ onUnauthorized }: ContextOptions = {}) {
	const unauthorized = (error: unknown) => {
		if (!(error instanceof ContentError && error.status === 401)) return;
		queryClient.setQueryData(meQuery().queryKey, null);
		onUnauthorized?.();
	};
	const queryClient: QueryClient = new QueryClient({
		queryCache: new QueryCache({
			onError: (error, query) => {
				// /auth/me answers null, not 401; the other auth calls are not the session's
				if (query.queryKey[0] !== "auth") unauthorized(error);
			},
		}),
		mutationCache: new MutationCache({ onError: unauthorized }),
		defaultOptions: {
			queries: {
				retry: (failures, error) => !isFinalError(error) && failures < 3,
			},
		},
	});
	return {
		queryClient,
	};
}

export function Provider({
	children,
	queryClient,
}: {
	children: React.ReactNode;
	queryClient: QueryClient;
}) {
	return (
		<QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
	);
}
